import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Plus, Play, Trash2 } from "lucide-react";

import {
  listCycleCountSchedules, createCycleCountSchedule,
  toggleCycleCountSchedule, deleteCycleCountSchedule,
  runCycleCountSamplerNow, listUpcomingSchedules, listTenantOperators,
} from "@/lib/cycle-count-schedules.functions";
import { listCustomersOptions, listZones } from "@/lib/admin-config.functions";
import { listFacilityLocations, listTenantSkus } from "@/lib/cycle-counts.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/app/admin/cycle-count-schedules")({
  component: SchedulesPage,
});

const SCOPES = [
  { v: "abc",      label: "ABC class (Pareto)",     hint: "A items often, C items rarely" },
  { v: "facility", label: "Whole facility (tenant)", hint: "Random sample across the site" },
  { v: "zone",     label: "Zone",                    hint: "All SKUs in one zone" },
  { v: "location", label: "Bin / location",          hint: "Everything in one bin" },
  { v: "customer", label: "Customer",                hint: "One brand's SKUs" },
  { v: "sku",      label: "Single SKU",              hint: "One SKU recurring" },
] as const;
type Scope = (typeof SCOPES)[number]["v"];

const CADENCE_PRESETS = [
  { v: "daily",   label: "Daily",   days: 1 },
  { v: "weekly",  label: "Weekly",  days: 7 },
  { v: "monthly", label: "Monthly", days: 30 },
  { v: "custom",  label: "Custom",  days: null as number | null },
] as const;
type CadencePreset = (typeof CADENCE_PRESETS)[number]["v"];

function SchedulesPage() {
  const qc = useQueryClient();
  const { tenantId, facilityId } = useScope();

  const listFn      = useServerFn(listCycleCountSchedules);
  const createFn    = useServerFn(createCycleCountSchedule);
  const toggleFn    = useServerFn(toggleCycleCountSchedule);
  const deleteFn    = useServerFn(deleteCycleCountSchedule);
  const runNowFn    = useServerFn(runCycleCountSamplerNow);
  const upcomingFn  = useServerFn(listUpcomingSchedules);
  const customersFn = useServerFn(listCustomersOptions);
  const zonesFn     = useServerFn(listZones);
  const locsFn      = useServerFn(listFacilityLocations);
  const skusFn      = useServerFn(listTenantSkus);
  const opsFn       = useServerFn(listTenantOperators);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<Scope>("facility");
  const [scopeId, setScopeId] = useState("");
  const [cadencePreset, setCadencePreset] = useState<CadencePreset>("weekly");
  const [customDays, setCustomDays] = useState("14");
  const [sample, setSample] = useState("25");
  const [blind, setBlind] = useState(false);
  const [assignedTo, setAssignedTo] = useState<string>("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["cc-schedules", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const { data: upcoming = [] } = useQuery({
    queryKey: ["cc-upcoming", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => upcomingFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const { data: customers = [] } = useQuery({
    queryKey: ["cc-customers", tenantId],
    enabled: !!tenantId && open && scope === "customer",
    queryFn: () => customersFn(),
  });
  const { data: zones = [] } = useQuery({
    queryKey: ["cc-zones", facilityId],
    enabled: !!facilityId && open && scope === "zone",
    queryFn: () => zonesFn({ data: { facilityId: facilityId! } }),
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["cc-locs", facilityId],
    enabled: !!facilityId && open && scope === "location",
    queryFn: () => locsFn({ data: { facilityId: facilityId! } }),
  });
  const { data: skus = [] } = useQuery({
    queryKey: ["cc-skus", tenantId],
    enabled: !!tenantId && open && scope === "sku",
    queryFn: () => skusFn({ data: { tenantId: tenantId! } }),
  });
  const { data: operators = [] } = useQuery({
    queryKey: ["cc-ops", tenantId],
    enabled: !!tenantId && open,
    queryFn: () => opsFn({ data: { tenantId: tenantId! } }),
  });

  const cadenceDays = useMemo(() => {
    const preset = CADENCE_PRESETS.find((p) => p.v === cadencePreset);
    if (preset?.days) return preset.days;
    return Math.max(1, Math.min(365, Number(customDays) || 14));
  }, [cadencePreset, customDays]);

  const reset = () => {
    setName(""); setScope("facility"); setScopeId("");
    setCadencePreset("weekly"); setCustomDays("14"); setSample("25");
    setBlind(false); setAssignedTo("");
  };

  const needsScopeId = scope !== "abc" && scope !== "facility";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !facilityId) return;
    if (!name.trim()) return toast.error("Name required");
    if (needsScopeId && !scopeId) return toast.error("Pick a target");
    try {
      await runServerFn(() => createFn({ data: {
        tenantId, facilityId, name: name.trim(),
        scopeType: scope,
        scopeId: scopeId || null,
        cadenceDays,
        sampleSize: Math.max(1, Number(sample) || 25),
        blindCount: blind,
        assignedTo: assignedTo || null,
      } }), "Create schedule");
      toast.success("Schedule created");
      setOpen(false); reset();
      qc.invalidateQueries({ queryKey: ["cc-schedules", tenantId, facilityId] });
      qc.invalidateQueries({ queryKey: ["cc-upcoming", tenantId, facilityId] });
    } catch { /* toast handled */ }
  };

  const toggle = async (id: string, active: boolean) => {
    await runServerFn(() => toggleFn({ data: { id, active: !active } }), "Toggle schedule");
    qc.invalidateQueries({ queryKey: ["cc-schedules", tenantId, facilityId] });
    qc.invalidateQueries({ queryKey: ["cc-upcoming", tenantId, facilityId] });
  };
  const remove = async (id: string) => {
    if (!confirm("Delete schedule?")) return;
    await runServerFn(() => deleteFn({ data: { id } }), "Delete schedule");
    qc.invalidateQueries({ queryKey: ["cc-schedules", tenantId, facilityId] });
    qc.invalidateQueries({ queryKey: ["cc-upcoming", tenantId, facilityId] });
  };
  const runNow = async () => {
    const res = await runServerFn(() => runNowFn({ data: {} }), "Sampling");
    toast.success(`Created ${res?.countsCreated ?? 0} draft cycle counts`);
    qc.invalidateQueries({ queryKey: ["cc-upcoming", tenantId, facilityId] });
  };

  const opLabel = (id?: string | null) =>
    id ? (operators.find((o: any) => o.id === id)?.label ?? id.slice(0, 8)) : "—";
  const overdueCount = upcoming.filter((u: any) => u.overdue).length;

  return (
    <>
      <PageHeader
        title="Cycle Count Schedules"
        description="Recurring sampling rules. Each schedule auto-creates draft counts on its cadence."
        actions={
          <>
            <Button variant="outline" onClick={runNow}>
              <Play className="h-4 w-4 mr-2" /> Run sampler now
            </Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
              <DialogTrigger asChild>
                <Button disabled={!tenantId || !facilityId}>
                  <Plus className="h-4 w-4 mr-2" /> New schedule
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New cycle count schedule</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekly A-movers" />
                  </div>
                  <div className="space-y-2">
                    <Label>What to count</Label>
                    <Select value={scope} onValueChange={(v) => { setScope(v as Scope); setScopeId(""); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SCOPES.map((s) => (
                          <SelectItem key={s.v} value={s.v}>
                            <div className="flex flex-col">
                              <span>{s.label}</span>
                              <span className="text-xs text-muted-foreground">{s.hint}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {needsScopeId && (
                    <div className="space-y-2">
                      <Label>
                        {scope === "customer" && "Customer"}
                        {scope === "zone"     && "Zone"}
                        {scope === "location" && "Bin / location"}
                        {scope === "sku"      && "SKU"}
                      </Label>
                      <Select value={scopeId} onValueChange={setScopeId}>
                        <SelectTrigger>
                          <SelectValue placeholder={`Pick a ${scope}…`} />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {scope === "customer" && customers.map((c: any) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                          {scope === "zone" && zones.map((z: any) => (
                            <SelectItem key={z.id} value={z.id}>{z.code} — {z.name}</SelectItem>
                          ))}
                          {scope === "location" && locations.map((l: any) => (
                            <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>
                          ))}
                          {scope === "sku" && skus.map((s: any) => (
                            <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>How often</Label>
                      <Select value={cadencePreset} onValueChange={(v) => setCadencePreset(v as CadencePreset)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CADENCE_PRESETS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {cadencePreset === "custom" && (
                        <Input type="number" min={1} max={365} value={customDays}
                          onChange={(e) => setCustomDays(e.target.value)} placeholder="every N days" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Sample size</Label>
                      <Input type="number" min={1} max={500} value={sample} onChange={(e) => setSample(e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Assign to (optional)</Label>
                    <Select value={assignedTo || "_none"} onValueChange={(v) => setAssignedTo(v === "_none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Unassigned</SelectItem>
                        {operators.map((o: any) => (
                          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between border rounded-lg p-3">
                    <div>
                      <Label htmlFor="blind" className="cursor-pointer text-sm font-medium">Blind count</Label>
                      <p className="text-xs text-muted-foreground">Hide expected qty from the counter.</p>
                    </div>
                    <Switch id="blind" checked={blind} onCheckedChange={setBlind} />
                  </div>
                  <DialogFooter><Button type="submit">Create schedule</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <div className="p-4 sm:p-6 space-y-6">
        {upcoming.length > 0 && (
          <div className="border rounded-lg bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold">Upcoming</h3>
                <p className="text-xs text-muted-foreground">
                  Next scheduled samples
                  {overdueCount > 0 && <span className="ml-2 text-destructive">· {overdueCount} overdue</span>}
                </p>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {upcoming.slice(0, 6).map((u: any) => (
                <div key={u.id} className={`border rounded-md p-3 text-sm ${u.overdue ? "border-destructive/50 bg-destructive/5" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{u.name ?? "Auto"}</span>
                    <Badge variant="outline" className="text-xs">{u.scope_type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {u.next_run_at ? new Date(u.next_run_at).toLocaleString() : "pending"}
                  </div>
                  <div className="text-xs text-muted-foreground">Assignee: {opLabel(u.assigned_to)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead className="text-right">Cadence</TableHead>
                <TableHead className="text-right">Sample</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Blind</TableHead>
                <TableHead>Next run</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No schedules. Create one to start auto-sampling.</TableCell></TableRow>
              ) : rows.map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name ?? "—"}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline">{r.scope_type}</Badge>
                    {r.scope_id && <span className="ml-1 font-mono text-muted-foreground">{r.scope_id.slice(0, 8)}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.cadence_days ?? "—"}d</TableCell>
                  <TableCell className="text-right tabular-nums">{r.sample_size ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{opLabel(r.assigned_to)}</TableCell>
                  <TableCell>{r.blind_count ? <Badge>blind</Badge> : <span className="text-xs text-muted-foreground">no</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.next_run_at ? new Date(r.next_run_at).toLocaleString() : "pending"}
                  </TableCell>
                  <TableCell className="text-xs">{r.active ? "active" : <span className="text-muted-foreground">paused</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => toggle(r.id, r.active)}>
                      {r.active ? "Pause" : "Resume"}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(r.id)} aria-label="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}